import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { ReactFacebookLoginInfo } from 'react-facebook-login';
import type { GoogleLoginResponse, GoogleLoginResponseOffline } from 'react-google-login';
import { FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';
import CarouselDisplayTrigger from '@alltrails/amplitude/enums/CarouselDisplayTrigger';
import UpsellTrigger from '@alltrails/amplitude/enums/UpsellTrigger';
import logLogInScreenContinueButtonTapped from '@alltrails/amplitude/events/logLogInScreenContinueButtonTapped';
import logSignUpScreenContinueButtonTapped from '@alltrails/amplitude/events/logSignUpScreenContinueButtonTapped';
import logSignUpScreenDismissed from '@alltrails/amplitude/events/logSignUpScreenDismissed';
import logWebSignUpModalShown from '@alltrails/amplitude/events/logWebSignUpModalShown';
import useSetUser from '@alltrails/context/hooks/useSetUser';
import useUser from '@alltrails/context/hooks/useUser';
import { setCurrencyCode } from '@alltrails/context/slices/currencyCodeSlice';
import { setPlans } from '@alltrails/context/slices/plansSlice';
import type PurchaseTier from '@alltrails/core/types/PurchaseTier';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import { COLOR_BACKGROUND_PRIMARY } from '@alltrails/denali/tokens';
import useExperiments from '@alltrails/experiments/hooks/useExperiments';
import useExposureEvent from '@alltrails/experiments/hooks/useExposureEvent';
import DownloadInAppQRCode from '@alltrails/in-app/components/DownloadInAppQRCode';
import AppsFlyerQrCode from '@alltrails/install-prompt/components/AppsFlyerQrCode/AppsFlyerQrCode';
import ModalStep from '@alltrails/modal/components/Modal/ModalStep';
import MultiStepModal from '@alltrails/modal/components/Modal/MultiStepModal';
import useMultiStepModal from '@alltrails/modal/hooks/useMultiStepModal';
import type DeeplinkData from '@alltrails/types/DeeplinkData';
import type { User } from '@alltrails/types/User';
import CheckoutModalStep from '@alltrails/upsell/components/CheckoutModalStep/CheckoutModalStep';
import CheckoutWelcome from '@alltrails/upsell/components/CheckoutWelcome/CheckoutWelcome';
import PostAuthUpsellStep from '@alltrails/upsell/components/GenericPostAuthUpsell/PostAuthUpsellStep';
import { UpsellNavigationProvider } from '@alltrails/upsell/components/UpsellSystem/BaseUpsell/UpsellNavigationContext';
import GenericPeakUpsellStep from '@alltrails/upsell/components/UpsellSystem/GenericPeakUpsell/GenericPeakUpsellStep';
import useApplePopupHandler from '../../hooks/useApplePopupHandler';
import useFacebookHandler from '../../hooks/useFacebookHandler';
import useGoogleHandler from '../../hooks/useGoogleHandler';
import LoginResponse from '../../types/LoginResponse';
import AuthWelcomeStep from './AuthWelcomeStep';
import EmailCheck from './EmailCheck';
import Initial from './Initial';
import InitialMobile from './InitialMobile';
import InitialSimplified from './InitialSimplified';
import LogIn from './LogIn';
import MapTriggers from './MapTriggers';
import OAuthStep from './OAuthStep';
import PhotoTriggers from './PhotoTriggers';
import PlusFeatureTriggers from './PlusFeatureTriggers';
import Register from './Register';
import styles from './SignUpModal.module.scss';

type Props = {
  autoClose?: boolean;
  closeModal: (event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent) => void;
  deeplinkData?: DeeplinkData;
  heading?: ReactNode;
  // Override the computed first step so the modal can open directly on the email form.
  initialStep?: 'email-check' | 'login';
  isOpen: boolean;
  onSuccess: (user: User) => void;
  redirectUrl?: string;
  hideUpsell?: boolean;
  hideAppPromotion?: boolean;
  trigger: CarouselDisplayTrigger;
  upsellTrigger: UpsellTrigger;
};

type SignupModalSteps =
  | 'checkout'
  | 'email-check'
  | 'initial'
  | 'initial-simplified'
  | 'login'
  | 'oauth-signin'
  | 'peakUpsell'
  | 'qr-code'
  | 'register'
  | 'upsell'
  | 'auth-welcome'
  | 'checkout-welcome';

// Desktop: https://www.figma.com/file/gZaqAej8HzoFkyrjlQ4cNs/Integrating-Brand-Changes?node-id=251%3A16532
// Mobile: https://www.figma.com/file/gZaqAej8HzoFkyrjlQ4cNs/Integrating-Brand-Changes?node-id=250%3A16053

export default function SignUpModal({
  autoClose,
  closeModal,
  deeplinkData,
  heading,
  initialStep,
  isOpen,
  onSuccess,
  redirectUrl,
  hideUpsell = false,
  hideAppPromotion = false,
  trigger,
  upsellTrigger
}: Props) {
  const isMobile = useIsMobileSizedScreen();
  const modalRef = useRef<HTMLDivElement>(null);
  const applePopupHandler = useApplePopupHandler(isOpen);
  const facebookHandler = useFacebookHandler();
  const googleHandler = useGoogleHandler();
  const dispatch = useDispatch();
  const experiments = useExperiments();
  const welcomeModalVariant = experiments['web-growth-rm-welcome-modal']?.value;
  const isWelcomeModalTreatment = welcomeModalVariant !== undefined && welcomeModalVariant !== 'control';
  const isOnTrailPage = deeplinkData?.pageType === 'trail';
  const sendWelcomeExposure = useExposureEvent('web-growth-rm-welcome-modal');

  // Disable simplified auth when the "simplified auth retest" treatment is active
  const disableSimplifiedAuth = isMobile && experiments['web-growth-rm-simplified-auth-retest']?.value === 'treatment';
  const sendSimplifiedAuthExposure = useExposureEvent('web-growth-rm-simplified-auth-retest');

  // Contextual auth copy — Map trigger (mWeb) experiment (GROW-18025). On mWeb, when the modal is
  // shown via a Map-bucket trigger, replace the generic headline with action-specific map copy.
  const contextualMapCopyVariant = experiments['web-growth-rm-contextual-auth-copy-map-mweb']?.value;
  const isContextualMapCopyEligible = isMobile && MapTriggers.includes(trigger);
  const sendContextualMapCopyExposure = useExposureEvent('web-growth-rm-contextual-auth-copy-map-mweb');

  // Contextual auth copy — Photos trigger (mWeb) experiment (GROW-18026). On mWeb, when the modal is
  // shown via a Photos-bucket trigger, replace the generic headline with action-specific photos copy.
  // Map and Photos buckets are mutually exclusive by trigger, so a session is only eligible for one.
  const contextualPhotosCopyVariant = experiments['web-growth-rm-contextual-auth-copy-photos-mweb']?.value;
  const isContextualPhotosCopyEligible = isMobile && PhotoTriggers.includes(trigger);
  const sendContextualPhotosCopyExposure = useExposureEvent('web-growth-rm-contextual-auth-copy-photos-mweb');

  // Contextual auth copy — Map trigger (Desktop) experiment (GROW-18027). On desktop, when the modal is
  // shown via a Map-bucket trigger, replace the generic headline with action-specific map copy.
  const contextualMapDesktopCopyVariant = experiments['web-growth-rm-contextual-auth-copy-map-desktop']?.value;
  const isContextualMapDesktopCopyEligible = !isMobile && MapTriggers.includes(trigger);
  const sendContextualMapDesktopCopyExposure = useExposureEvent('web-growth-rm-contextual-auth-copy-map-desktop');

  // Contextual auth copy — Photos trigger (Desktop) experiment (GROW-18028). On desktop, when the modal is
  // shown via a Photos-bucket trigger, replace the generic headline with action-specific photos copy.
  const contextualPhotosDesktopCopyVariant = experiments['web-growth-rm-contextual-auth-copy-photos-desktop']?.value;
  const isContextualPhotosDesktopCopyEligible = !isMobile && PhotoTriggers.includes(trigger);
  const sendContextualPhotosDesktopCopyExposure = useExposureEvent('web-growth-rm-contextual-auth-copy-photos-desktop');

  // Only override the headline for eligible sessions and when a caller has not supplied its own heading.
  const contextualMapHeading = (() => {
    if (!isContextualMapCopyEligible || heading) {
      return undefined;
    }
    switch (contextualMapCopyVariant) {
      case 'treatment_1':
        return <FormattedMessage defaultMessage="Bring the trail map to life in the app" />;
      case 'treatment_2':
        return <FormattedMessage defaultMessage="Take the trail map with you in the app" />;
      case 'treatment_3':
        return <FormattedMessage defaultMessage="Explore the trail map in the app" />;
      default:
        return undefined;
    }
  })();

  const contextualPhotosHeading = (() => {
    if (!isContextualPhotosCopyEligible || heading) {
      return undefined;
    }
    switch (contextualPhotosCopyVariant) {
      case 'treatment_1':
        return <FormattedMessage defaultMessage="See the views before you go in the app" />;
      case 'treatment_2':
        return <FormattedMessage defaultMessage="Find photo-worthy spots in the app" />;
      case 'treatment_3':
        return <FormattedMessage defaultMessage="See more trail photos in the app" />;
      default:
        return undefined;
    }
  })();

  const contextualMapDesktopHeading = (() => {
    if (!isContextualMapDesktopCopyEligible || heading) {
      return undefined;
    }
    switch (contextualMapDesktopCopyVariant) {
      case 'treatment_1':
        return <FormattedMessage defaultMessage="Explore the map, log in or sign up for free" />;
      case 'treatment_2':
        return <FormattedMessage defaultMessage="Check the trail map, log in or sign up for free" />;
      case 'treatment_3':
        return <FormattedMessage defaultMessage="See the trail map, log in or sign up for free" />;
      default:
        return undefined;
    }
  })();

  const contextualPhotosDesktopHeading = (() => {
    if (!isContextualPhotosDesktopCopyEligible || heading) {
      return undefined;
    }
    switch (contextualPhotosDesktopCopyVariant) {
      case 'treatment_1':
        return <FormattedMessage defaultMessage="Find photo-worthy spots, log in or sign up for free" />;
      case 'treatment_2':
        return <FormattedMessage defaultMessage="See the views ahead, log in or sign up for free" />;
      case 'treatment_3':
        return <FormattedMessage defaultMessage="See trail photos, log in or sign up for free" />;
      default:
        return undefined;
    }
  })();

  const resolvedHeading = contextualMapHeading ?? contextualPhotosHeading ?? contextualMapDesktopHeading ?? contextualPhotosDesktopHeading ?? heading;

  // Resource info for the SMS "text me a link" form inside the QR step.
  // The QR image itself is a static asset because the dynamic QR generation API requires an authenticated user currently
  const smsDeeplinkResource =
    typeof deeplinkData?.pageId === 'number' && deeplinkData.pageType === 'trail'
      ? { resourceType: 'trail' as const, resourceId: deeplinkData.pageId }
      : undefined;

  const isDesktopAppCtaEligible = !isMobile && isOnTrailPage;

  const computedInitialStep: SignupModalSteps = (() => {
    if (isMobile) {
      return disableSimplifiedAuth ? 'initial' : 'initial-simplified';
    }
    return isDesktopAppCtaEligible ? 'initial-simplified' : 'initial';
  })();

  const resolvedInitialStep: SignupModalSteps = initialStep ?? computedInitialStep;

  const { activeModal, navigateTo, goBack } = useMultiStepModal<SignupModalSteps>(resolvedInitialStep);
  const setUser = useSetUser();
  const user = useUser();

  // Set by whichever step enters checkout, so a Peak cross-sell purchase is not billed as Plus.
  const [checkoutTier, setCheckoutTier] = useState<PurchaseTier>('plus');
  // Existing Peak subscribers have nothing to cross-sell; the post-auth upsell is always Plus otherwise.
  const isPeakCrossSellEligible = user?.subscriptionTier !== 'peak';
  const navigateFrom = (tier: PurchaseTier) => (step: SignupModalSteps) => {
    if (step === 'checkout') {
      setCheckoutTier(tier);
    }

    navigateTo(step);
  };

  const [prefillEmail, setPrefillEmail] = useState<string>();
  const [oauthProvider, setOauthProvider] = useState<'Apple' | 'Google' | 'Facebook'>();
  const didLogMountRef = useRef(false);

  useEffect(() => {
    if (typeof window !== 'undefined' && 'Cypress' in window && !didLogMountRef.current) {
      didLogMountRef.current = true;
      console.log(`[SignUpModal] mount at ${Math.round(performance.now())}ms`, { isOpen, trigger, readyState: document.readyState });
    }
  }, [isOpen, trigger]);

  const successCallback = useCallback(
    async ({ user, plans, currencyCode, is_sign_up }: LoginResponse) => {
      if (redirectUrl) {
        window.location.assign(redirectUrl);
      } else {
        setUser(user);
        dispatch(setPlans(plans));
        dispatch(setCurrencyCode(currencyCode));

        global.google?.accounts.id.cancel(); // google can be undefined on mobile web

        const isInUpsellTier = user?.subscriptionTier === 'base';
        const shouldShowUpsell = !hideUpsell && isInUpsellTier && (!trigger || !PlusFeatureTriggers.includes(trigger));

        // Fires the exposure event for eligible welcome xp variants (including control)
        const isEligibleForWelcomeExperiment = isOnTrailPage && shouldShowUpsell && is_sign_up;
        if (isEligibleForWelcomeExperiment) {
          sendWelcomeExposure();
        }

        const shouldShowWelcome = isWelcomeModalTreatment && isEligibleForWelcomeExperiment;

        if (shouldShowWelcome) {
          navigateTo('auth-welcome');
        } else if (shouldShowUpsell) {
          navigateTo('upsell');
        } else {
          onSuccess(user);
          autoClose && closeModal();
        }
      }
    },
    [
      redirectUrl,
      setUser,
      hideUpsell,
      trigger,
      dispatch,
      navigateTo,
      onSuccess,
      autoClose,
      closeModal,
      isWelcomeModalTreatment,
      isOnTrailPage,
      sendWelcomeExposure
    ]
  );

  const handleCloseRequest = () => {
    logSignUpScreenDismissed({ signup_screen_dismissed_type: 'x' });
    closeModal();
  };

  const getLoginHandlers = useCallback(
    (isLogIn: boolean) => {
      const appleLoginHandler = async () => {
        if (isLogIn) {
          logLogInScreenContinueButtonTapped({ login_screen_continue_button_type: 'apple' });
        } else {
          logSignUpScreenContinueButtonTapped({ signup_screen_continue_button_type: 'apple' });
        }

        try {
          const loginResponse = await applePopupHandler({ handlerType: isLogIn ? 'login' : 'signup' });
          successCallback(loginResponse as LoginResponse);
        } catch {
          // no specific UI provided by design
        }
      };

      const googleLoginHandler = async (response: GoogleLoginResponse | GoogleLoginResponseOffline | string) => {
        const loginResponse = await googleHandler({ googleUserOrToken: response, handlerType: isLogIn ? 'login' : 'signup' });
        successCallback(loginResponse);
      };

      const facebookLoginHandler = async (response: ReactFacebookLoginInfo) => {
        try {
          const loginResponse = await facebookHandler({
            loginInfo: response,
            handlerType: isLogIn ? 'login' : 'signup'
          });
          successCallback(loginResponse);
        } catch {
          // no specific UI provided by design
        }
      };

      return { appleLoginHandler, googleLoginHandler, facebookLoginHandler };
    },
    [applePopupHandler, facebookHandler, googleHandler, successCallback]
  );

  function getActiveModal() {
    switch (activeModal) {
      case 'login':
        return (
          <ModalStep onCloseRequest={handleCloseRequest}>
            <LogIn handleLoginSuccess={successCallback} prefillEmail={prefillEmail} {...getLoginHandlers(true)} />
          </ModalStep>
        );

      case 'register':
        return (
          <ModalStep onCloseRequest={handleCloseRequest}>
            <Register
              onSuccess={successCallback}
              prefillEmail={prefillEmail}
              onEmailAlreadyExists={email => {
                setPrefillEmail(email);
                navigateTo('login');
              }}
            />
          </ModalStep>
        );

      case 'email-check':
        return (
          <ModalStep onCloseRequest={handleCloseRequest}>
            <EmailCheck
              heading={resolvedHeading}
              onEmailAvailable={email => {
                setPrefillEmail(email);
                navigateTo('register');
              }}
              onEmailExists={email => {
                setPrefillEmail(email);
                navigateTo('login');
              }}
              onOAuthRequired={(email, provider) => {
                setPrefillEmail(email);
                setOauthProvider(provider);
                navigateTo('oauth-signin');
              }}
            />
          </ModalStep>
        );

      case 'oauth-signin':
        return (
          <ModalStep onCloseRequest={handleCloseRequest}>
            <OAuthStep provider={oauthProvider!} {...getLoginHandlers(true)} />
          </ModalStep>
        );

      case 'initial':
        return (
          <ModalStep onCloseRequest={handleCloseRequest} hideBackButton={isMobile}>
            {isMobile ? (
              <InitialMobile
                heading={resolvedHeading}
                displayTrigger={trigger}
                deeplinkData={deeplinkData}
                hideAppPromotion={hideAppPromotion}
                onContinueWithEmailClick={() => {
                  logSignUpScreenContinueButtonTapped({ signup_screen_continue_button_type: 'email' });
                  navigateTo('email-check');
                }}
                {...getLoginHandlers(false)}
              />
            ) : (
              <Initial
                heading={resolvedHeading}
                onContinueInAppClick={smsDeeplinkResource ? () => navigateTo('qr-code') : undefined}
                onContinueWithEmailClick={() => {
                  logSignUpScreenContinueButtonTapped({ signup_screen_continue_button_type: 'email' });
                  navigateTo('email-check');
                }}
                showAppCta={isDesktopAppCtaEligible}
                {...getLoginHandlers(false)}
              />
            )}
          </ModalStep>
        );

      case 'initial-simplified':
        return (
          <ModalStep onCloseRequest={handleCloseRequest} hideBackButton>
            <InitialSimplified
              heading={resolvedHeading}
              displayTrigger={trigger}
              deeplinkData={deeplinkData}
              onContinueInAppClick={smsDeeplinkResource ? () => navigateTo('qr-code') : undefined}
              onMoreOptionsClick={() => {
                logSignUpScreenContinueButtonTapped({ signup_screen_continue_button_type: 'more options' });
                navigateTo('initial');
              }}
              googleLoginHandler={getLoginHandlers(false).googleLoginHandler}
              hideAppPromotion={hideAppPromotion}
            />
          </ModalStep>
        );

      case 'qr-code': {
        // The qr-code step is accessible when smsDeeplinkResource is defined, so we can expect it to be defined here
        const { resourceId, resourceType } = smsDeeplinkResource!;
        return (
          <ModalStep onCloseRequest={handleCloseRequest}>
            <DownloadInAppQRCode
              deeplink="open"
              customQrCode={<AppsFlyerQrCode trailId={resourceId} size={180} logoBackgroundColor={COLOR_BACKGROUND_PRIMARY} />}
              resourceId={resourceId}
              resourceType={resourceType}
              subtitle={<FormattedMessage defaultMessage="Scan the QR code to open this trail in the app" />}
              title={<FormattedMessage defaultMessage="Open in the app" />}
              webToAppAction="open in app"
            />
          </ModalStep>
        );
      }

      case 'auth-welcome': {
        // Treatment 'upsell': both "Continue to trail" and dismiss (X) lead to the upsell.
        // Treatments 'no-upsell & 'choice': "Continue to trail" and dismiss (X) bypass the upsell and reach the trail.
        const navigateToUpsell = () => navigateTo('upsell');
        const continueToTrail = () => {
          if (user) {
            onSuccess(user);
          }
          closeModal();
        };

        const isUpsellVariant = welcomeModalVariant === 'upsell';

        return (
          <AuthWelcomeStep
            onCloseRequest={isUpsellVariant ? navigateToUpsell : continueToTrail}
            onContinue={isUpsellVariant ? navigateToUpsell : continueToTrail}
            onTryPlus={welcomeModalVariant === 'choice' ? navigateToUpsell : undefined}
          />
        );
      }

      case 'upsell': {
        const upsellStep = (
          <PostAuthUpsellStep navigateTo={navigateFrom('plus')} onCloseRequest={closeModal} onSuccess={onSuccess} upsellTrigger={upsellTrigger} />
        );
        return isPeakCrossSellEligible ? (
          <UpsellNavigationProvider navigateToPeakUpsell={() => navigateTo('peakUpsell')}>{upsellStep}</UpsellNavigationProvider>
        ) : (
          upsellStep
        );
      }

      case 'peakUpsell':
        return (
          <GenericPeakUpsellStep
            navigateTo={navigateFrom('peak')}
            onCloseRequest={closeModal}
            onSuccess={() => {
              if (user) {
                onSuccess(user);
              }
            }}
            upsellTrigger={upsellTrigger}
          />
        );

      case 'checkout':
        return (
          <CheckoutModalStep
            closeModal={closeModal}
            navigateToWelcome={() => navigateTo('checkout-welcome')}
            purchaseTier={checkoutTier}
            trigger={upsellTrigger}
          />
        );

      case 'checkout-welcome':
        return (
          <CheckoutWelcome
            closeModal={user => {
              onSuccess(user);
              closeModal();
            }}
            purchaseTier={checkoutTier}
          />
        );
    }
  }

  useEffect(() => {
    if (typeof window !== 'undefined' && 'Cypress' in window) {
      console.log(`[SignUpModal] isOpen at ${Math.round(performance.now())}ms`, { isOpen, trigger, readyState: document.readyState });
    }
    if (isOpen) {
      logWebSignUpModalShown({ trigger });
    }
  }, [isOpen, trigger]);

  useEffect(() => {
    if (isOpen && isMobile) {
      sendSimplifiedAuthExposure();
    }
  }, [isOpen, isMobile, sendSimplifiedAuthExposure]);

  // Fire the contextual map copy exposure for all eligible mWeb map-trigger sessions (including control).
  useEffect(() => {
    if (isOpen && isContextualMapCopyEligible) {
      sendContextualMapCopyExposure();
    }
  }, [isOpen, isContextualMapCopyEligible, sendContextualMapCopyExposure]);

  // Fire the contextual photos copy exposure for all eligible mWeb photos-trigger sessions (including control).
  useEffect(() => {
    if (isOpen && isContextualPhotosCopyEligible) {
      sendContextualPhotosCopyExposure();
    }
  }, [isOpen, isContextualPhotosCopyEligible, sendContextualPhotosCopyExposure]);

  // Fire the contextual map copy exposure for all eligible desktop map-trigger sessions (including control).
  useEffect(() => {
    if (isOpen && isContextualMapDesktopCopyEligible) {
      sendContextualMapDesktopCopyExposure();
    }
  }, [isOpen, isContextualMapDesktopCopyEligible, sendContextualMapDesktopCopyExposure]);

  // Fire the contextual photos copy exposure for all eligible desktop photos-trigger sessions (including control).
  useEffect(() => {
    if (isOpen && isContextualPhotosDesktopCopyEligible) {
      sendContextualPhotosDesktopCopyExposure();
    }
  }, [isOpen, isContextualPhotosDesktopCopyEligible, sendContextualPhotosDesktopCopyExposure]);

  // Determine which steps should have the taller modal class
  const tallerSteps: SignupModalSteps[] = isMobile
    ? ['initial', 'upsell', 'peakUpsell', 'checkout', 'checkout-welcome']
    : ['upsell', 'peakUpsell', 'checkout', 'checkout-welcome'];

  return (
    <MultiStepModal
      backgroundClassName={styles.background}
      ref={modalRef}
      isFullHeight={activeModal === 'checkout' || (activeModal === 'auth-welcome' && isMobile)}
      isOpen={isOpen}
      onBackRequest={activeModal === 'upsell' || activeModal === 'auth-welcome' || activeModal === 'checkout-welcome' ? undefined : goBack}
      isPaused={activeModal === 'checkout'}
      onCloseRequest={closeModal}
      onCloseComplete={isMobile ? () => navigateTo(resolvedInitialStep) : undefined}
      focusTrapOptions={{
        allowOutsideClick: e => e.target !== modalRef.current,
        clickOutsideDeactivates: false,
        preventScroll: isWelcomeModalTreatment // Prevent auto-scroll since we scroll the user to the top in ScrollLimit and AuthWelcomeStep components
      }} // DISCO-10566 don't close the signup modal when install banner and signup modal both appear and user clicks outside of banner
      {...(tallerSteps.includes(activeModal) && !hideAppPromotion ? { modalClassName: styles.taller } : {})}
    >
      {getActiveModal()}
    </MultiStepModal>
  );
}
